import { useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Loader2, ShieldAlert } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const mb = (n) => `${(n / 1048576).toFixed(1)} MB`;

// Admin only: full database backup (mongodump format) streamed to this browser. Never a link, never stored elsewhere.
export function FullDbBackup() {
  const api = useApi();
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [got, setGot] = useState(0);
  const [res, setRes] = useState(null);
  const run = async () => {
    setAsk(false); setBusy(true); setGot(0); setRes(null);
    try {
      const r = await api.post("/admin/db-backup", {}, { responseType: "blob", timeout: 0, onDownloadProgress: (e) => setGot(e.loaded || 0) });
      const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "musora_db_backup.tar.gz";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setRes({ name, size: r.data.size, sha: r.headers["x-backup-sha256"], docs: r.headers["x-backup-documents"], cols: r.headers["x-backup-collections"] });
      toast.success("Backup scaricato");
    } catch (e) {
      let msg = errMsg(e);
      if (e?.response?.data instanceof Blob) { try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep generic */ } }
      toast.error(msg);
    }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="full-db-backup">
      <p className="text-sm text-slate-700">Copia fedele di tutto il database (BSON con indici, ripristinabile con <code>mongorestore</code>), con un manifest dei conteggi e lo SHA-256 di ogni file.
        Il file si scarica <b>solo in questo browser</b>: non viene salvato altrove e non esiste un link da condividere. L'operazione viene registrata nel registro attività.</p>
      <Button onClick={() => setAsk(true)} disabled={busy} data-testid="full-db-backup-start">
        {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <DatabaseBackup size={16} className="mr-2" />}{busy ? `Preparazione e download… ${got ? mb(got) : ""}` : "Backup completo database"}</Button>
      {res && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="full-db-backup-result">
        <p><b>{res.name}</b> · {mb(res.size)} · {res.cols} collection · {res.docs} documenti</p>
        <p className="mt-1 break-all font-mono text-xs">SHA-256: {res.sha}</p></div>}
      <AlertDialog open={ask} onOpenChange={setAsk}>
        <AlertDialogContent data-testid="full-db-backup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert size={18} className="text-amber-600" />Scaricare il backup completo?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-700">Il file contiene <b>tutti i dati</b>, compresi i dati personali dei clienti. Conservalo cifrato e non inviarlo per chat o email.
              Durante il backup evita modifiche importanti ai dati: non è un'istantanea perfetta.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="full-db-backup-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={run} data-testid="full-db-backup-confirm">Scarica il backup</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>);
}
